import { useTranslations } from '@/i18n/hooks';
import { useTheme } from '@/lib/theme/ThemeContext';
import { Link } from 'react-router-dom';
import { UserCircle, UsersRound } from 'lucide-react';
import { motion } from 'framer-motion';

export function RoleSelection() {
  const translate = useTranslations();
  const { resolvedTheme } = useTheme();

  // Translation keys with fallback values
  const translations = {
    chooseRole: translate('auth.chooseRole') || 'Choose Your Role',
    roleSelectionInfo: translate('auth.roleSelectionInfo') || 'Select how you want to use SleepNest',
    parentRole: translate('auth.parentRole') || 'Parent/Caregiver',
    parentRoleDescription: translate('auth.parentRoleDescription') || 'Track your child\'s sleep patterns and get personalized guidance from sleep consultants.',
    registerAsParent: translate('auth.registerAsParent') || 'Register as a Parent',
    specialistRole: translate('auth.specialistRole') || 'Sleep Consultant',
    specialistRoleDescription: translate('auth.specialistRoleDescription') || 'Help parents improve their children\'s sleep while growing your sleep consulting practice.',
    registerAsSpecialist: translate('auth.registerAsSpecialist') || 'Register as a Consultant'
    // Removed sign-in related translations as they're no longer used
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#141414]">
      <div className="container max-w-4xl px-6 py-12">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold mb-3 text-white">
            {translations.chooseRole}
          </h1>
          <p className="text-lg text-gray-300">
            {translations.roleSelectionInfo}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Caregiver Option */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-xl overflow-hidden border bg-gray-800 border-gray-700 shadow-md hover:shadow-lg transition-all duration-300"
          >
            <div className="p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto bg-blue-900/30 text-blue-400">
                <UserCircle size={32} />
              </div>
              <h2 className="text-2xl font-semibold text-center mb-4 text-white">
                {translations.parentRole}
              </h2>
              <p className="text-center mb-4 text-gray-300">
                {translations.parentRoleDescription}
              </p>
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-300 text-center">
                  📱 <strong>Mobile App Access</strong> - Get the SleepNest mobile experience
                </p>
              </div>
              <Link 
                to="/whatsapp-community" 
                className="w-full flex items-center justify-center py-3 px-4 rounded-md font-medium transition-colors bg-blue-600 hover:bg-blue-500 text-white"
              >
                {translations.registerAsParent}
              </Link>
            </div>
          </motion.div>

          {/* Specialist Option */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="rounded-xl overflow-hidden border bg-gray-800 border-gray-700 shadow-md hover:shadow-lg transition-all duration-300"
          >
            <div className="p-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto bg-purple-900/30 text-purple-400">
                <UsersRound size={32} />
              </div>
              <h2 className="text-2xl font-semibold text-center mb-4 text-white">
                {translations.specialistRole}
              </h2>
              <p className="text-center mb-4 text-gray-300">
                {translations.specialistRoleDescription}
              </p>
              <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-purple-300 text-center">
                  💻 <strong>Web Platform Only</strong> - Professional dashboard access
                </p>
              </div>
              <Link 
                to="/specialist-registration" 
                className="w-full flex items-center justify-center py-3 px-4 rounded-md font-medium transition-colors bg-purple-600 hover:bg-purple-500 text-white"
              >
                {translations.registerAsSpecialist}
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Sign-in options removed */}
      </div>
    </div>
  );
}
